import { ref } from "vue";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";
import { UrlUtils } from "@sazka/utils/url";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { NgBalance, NgFw, NgSessionLite } from "./model/ng-fw";
import { useNgSession } from "./ng-session";
import { logLogin } from "@sazka/utils/log-login";
import { ngHandleMmi } from "./ng-mmi";
import { useNg3DsRedirection } from "./ng-3ds-redirection";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { ngSessionToContact } from "./ng-session-to-contact";

export const ngFrameworkLoadedSuccessfully = ref(false);
export const isNgUIVisible = ref(false);

let hasNgFrameworkError = false;

const frameworkLoadedPromiseResolves: (() => void)[] = [];
const sessionStatusCallbacks: ((session: NgSessionLite) => void)[] = [];
let openPageCallback: (key: string, customInfo: string | any) => void = null;

export async function useNg(openPage: typeof openPageCallback) {
    openPageCallback = openPage;
    useNgSession().init();
    useNgLoader();
    // await ngFrameworkLoaded();
}

/**
 * ng_hs is ng framework's way to notify web page when user state changes
 * it is using callbacks
 */
async function useNgHs() {
    try {
        ng_hs.onBalanceUpdated((balance: NgBalance) => {
            useNgSession().balance.value = balance;
        });

        ng_hs.onSessionStatusUpdate((session: NgSessionLite) => {
            if (session.Status == "Login") {
                // we need to do that to load more information from NG framework
                useNgSession().refreshSession();
                const { session } = useNgSession();

                logLogin(ngSessionToContact(session.value), session.value.PlayerID);
            } else {
                useNgSession().clearSession();
            }
            sessionStatusCallbacks.forEach((f) => f(session));
        });

        ng_hs.registrationAborted(() => eventBus.emit(GlobalEvents.WebLoginAborted));

        ng_hs.openPage(openPageCallback);

        ng_hs.clearURLParams((urlParams: string[]) => {
            UrlUtils.clearUrlParams(urlParams);
        });

        ng_hs.onStateChange((state) => {
            isNgUIVisible.value = state === "active";
        });

        useNg3DsRedirection().registerNgHsCallback();
    } catch (e: any) {
        logException("neogames", e, { message: "An error has occurred while adding ng_hs callbacks" });
    }
}

export function getNgTimeDuration(ngActionTimestamp: string, ngServerTime: string): number {
    const start = Date.parse(ngActionTimestamp);
    const server = Date.parse(ngServerTime);
    const now = Date.now();
    return start - server + now;
}

function useNgIdentityVerification() {
    const parameter = UrlUtils.getQueryString("identityverification", null, false);
    if (parameter) {
        ng_fw.showPersonalInfoVerification();
    }
}

function useNgLoader() {
    if (ngLoader.ngIsReady()) {
        frameworkLoaded();
    } else if (ngLoader.ngError) {
        frameworkLoadError(ngLoader.ngErrorMessage);
    } else {
        document.addEventListener("fwInitError", (reason) => frameworkLoadError(reason as any));
        document.addEventListener("fwReady", frameworkLoaded);
    }
}

export function ngFrameworkLoaded(): Promise<void> {
    return new Promise((res, rej) => {
        if (ngFrameworkLoadedSuccessfully.value) {
            res();
        } else if (hasNgFrameworkError) {
            rej();
        } else {
            frameworkLoadedPromiseResolves.push(res);
        }
    });
}

/**
 * @deprecated Use watch on useNgSession().isLoggedIn instead
 * @param f callback
 */
export function addSessionStatusUpdateCallback(f: (status: NgSessionLite) => void): void {
    sessionStatusCallbacks.push(f);
}

async function frameworkLoaded() {
    ng_fw.keepAlive();
    ngFrameworkLoadedSuccessfully.value = true;
    useNgHs();
    ngHandleMmi();
    useNgIdentityVerification();

    const { refreshSession, refreshBalance, restoreFromWebView } = useNgSession();
    refreshSession();
    await refreshBalance();
    await restoreFromWebView();
    for (const resolve of frameworkLoadedPromiseResolves) {
        resolve();
    }
}

function frameworkLoadError(errorMessage: string) {
    ngFrameworkLoadedSuccessfully.value = false;
    hasNgFrameworkError = true;
    logException("NG framework", errorMessage);
    showAlert({
        title: "Tady něco nehraje…",
        subText:
            "Je nám líto, ale nepodařilo se správně načíst stránku. Pokud i poté potíže přetrvávají, jedná se o chybu na naší straně, na jejímž odstranění pracujeme.",
    });
}

export function ngAction(actionName: keyof NgFw) {
    if (ngFrameworkLoadedSuccessfully.value) {
        (ng_fw[actionName] as any)();
    }
}
